import {} from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { useTimeEntryActionBarState } from 'components/domain/time-entry/TimeEntryActionBar/TimeEntryActionBarContext';
import DateTimeEntryDataReport from 'components/domain/time-entry/TimeEntryDataReport/DateTimeEntryDataReport/DateTimeEntryDataReport';
import useDateTimeEntryDataReport from 'components/domain/time-entry/TimeEntryDataReport/DateTimeEntryDataReport/hooks/useDateTimeEntryDataReport';
import { ITimeEntryDataTableRow } from 'components/domain/time-entry/TimeEntryDataTable/TimeEntryDataTable';
import { defaultTimeEntryColumns } from 'containers/timesheets/hooks/useTimesheetsColumns';
import { usePrintComplexDataChannelListener } from 'contexts/ChannelContext/hooks/usePrintComplexDataChannelListener';
import { useReduxSelector } from 'hooks';
import useBrandTitle from 'hooks/meta/useBrandTitle';
import { isEmpty, isNil, isNull, map, partition } from 'lodash';
import { DateTime } from 'luxon';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { updateCheckedTimeEntryIds, updateCheckedTimeOffIds } from 'store/TimeEntryTable/TimeEntryTable';
import { ClassName } from 'types/ClassName';
import ITimeRange from 'types/TimeRange';
import { dateUtils } from 'utils';
import { t } from 'utils/localize';
import { useTimeEntryExportChannelListener } from '../hooks/useTimeEntryExportChannelListener';

export interface IDateTimeEntryTableListeningContainerProps {
  className?: ClassName;
  timeRange: ITimeRange<DateTime>;
  equipmentIds?: string[];
  memberIds?: string[];
  projectIds?: string[];
  costCodeIds?: string[];
  projectIdWithDescendants?: boolean;
  applyPrintOptions: boolean;
  includeAddOns?: boolean;
}

const DateTimeEntryTableListeningContainer = (props: IDateTimeEntryTableListeningContainerProps) => {
  const {
    className,
    timeRange,
    equipmentIds,
    memberIds,
    projectIds,
    costCodeIds,
    projectIdWithDescendants,
    applyPrintOptions,
    includeAddOns,
  } = props;

  const dispatch = useDispatch();
  const selectedIds = useReduxSelector((state) =>
    state.timeEntryTable.checkedTimeEntryIds.concat(state.timeEntryTable.checkedTimeOffIds)
  );

  const onSelect = useCallback(
    (rows: ITimeEntryDataTableRow[]) => {
      const [timeEntryIds, timeOffIds] = partition(rows, (row) => isNil(row.timeOffType));
      dispatch(updateCheckedTimeEntryIds(map(timeEntryIds, 'id')));
      dispatch(updateCheckedTimeOffIds(map(timeOffIds, 'id')));
    },
    [dispatch]
  );

  const { data, loading, getData } = useDateTimeEntryDataReport(
    timeRange,
    false,
    costCodeIds,
    equipmentIds,
    memberIds,
    projectIds,
    projectIdWithDescendants,
    includeAddOns
  );

  const [timeEntryActionBarState] = useTimeEntryActionBarState();
  const brand = useBrandTitle();

  useEffect(() => {
    if (!isEmpty(timeEntryActionBarState.entries)) {
      getData();
    }
    if (!isEmpty(timeEntryActionBarState.memberIds)) {
      getData();
    }
    if (!isNull(timeEntryActionBarState.formData)) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeEntryActionBarState]);

  useTimeEntryExportChannelListener(
    data,
    !loading,
    `${brand}-${t('activity-report-time-entries')}-${dateUtils.isoTimeStampLocal()}`,
    undefined,
    includeAddOns
  );
  usePrintComplexDataChannelListener(data, !loading);

  const classes = classNames('date-time-entry-table-container', className);

  return (
    <DateTimeEntryDataReport
      className={classes}
      columnSettings={defaultTimeEntryColumns}
      updateDates={getData}
      timeRange={timeRange}
      selectedIds={selectedIds}
      onSelectChange={onSelect}
      data={data}
      loading={loading}
      memberIds={memberIds}
      loadedAll={!loading}
      applyPrintOptions={applyPrintOptions}
    />
  );
};

export default DateTimeEntryTableListeningContainer;
